import { DetailTitleComponent, CustomButton } from '@/shared/ui/components';
import { SUPPORT_REQUEST_STATUS_ENUM } from '@/shared/config';
import { SUPPORT_REQUEST_TYPE } from '@/entities/support';
import { zeroPad } from '@/shared/lib';
import classNames from 'classnames';
import { useMemo } from 'react';
import { t } from 'ttag';
import { useHeaderPage } from './useHeaderPage';
import type { HeaderPageProps } from './headerPage.types';
import './headerPage.scss';

/**
 * Header of the support request detail page. Derives the title blocks from detailData - the zero-padded ticket id with an Open or Resolved label plus the request type resolved from SUPPORT_REQUEST_TYPE - hands them to DetailTitleComponent with a back link to /support and the re-open action, and adds a box-shadow class while useHeaderPage reports the page scrolled.
 *
 * @param isLoadedData - Whether the detail data has finished loading.
 * @param detailData - Support request record shown in the header.
 * @param handleReOpen - Callback re-opening the support request.
 * @returns The detail page header element.
 */
const HeaderPage = ({ isLoadedData, detailData, handleReOpen }: HeaderPageProps) => {
  const { status, ticketId, category } = detailData || {};

  // const headerRef = useRef();
  // const isChangePosition = useElementChange(headerRef);

  const { state, onClickReOpen } = useHeaderPage({ handleReOpen });

  /**
   * Title blocks rendered in the support request detail header. Pairs the zero-padded request ID plus an Open/Resolved label derived from the ticket status with the request type resolved from SUPPORT_REQUEST_TYPE by category, recomputed whenever detailData changes.
   *
   * @returns Array of title/content pairs for the detail title component.
   */
  const titleBlockArray = useMemo(() => {
    const isOpen = status === SUPPORT_REQUEST_STATUS_ENUM.New;
    const contentRequest = (
      <span className="content-request">
        {zeroPad(ticketId)}
        <span className={classNames('label', isOpen ? 'open' : 'resolved')}>
          {isOpen ? t`Open` : t`Resolved`}
        </span>
      </span>
    );

    const studyType = SUPPORT_REQUEST_TYPE?.[category];

    const titleBlockArray = [
      {
        title: t`Request ID`,
        content: contentRequest,
      },
      {
        title: t`Request type`,
        content: studyType,
      },
    ];
    return titleBlockArray;
  }, [detailData]);

  /**
   * Builds the right-hand action of the support request detail header. Returns null while the ticket status is New, otherwise an outline Re-open button wired to onClickReOpen that reflects state.isSavingButton as its loading state.
   *
   * @returns The re-open button element, or null for a ticket still open.
   */
  const generateRightButton = () => {
    const isOpen = status === SUPPORT_REQUEST_STATUS_ENUM.New;
    if (isOpen) {
      return null;
    }
    return (
      <CustomButton
        id="re-open-btn"
        type="outline"
        onClick={onClickReOpen}
        loading={state.isSavingButton}
        size="small"
      >
        {t`Re-open`}
      </CustomButton>
    );
  };

  // useEffect(() => {
  //   // calculate distance from header to to so need to fixed body
  //   let leftElement;
  //   let rightElement;
  //   if (headerRef.current) {
  //     const boundingElement = headerRef.current.getBoundingClientRect();
  //     const positionTop = boundingElement.top + boundingElement.height;

  //     leftElement = document.querySelector('#body-left');
  //     rightElement = document.querySelector('#body-right');
  //     if (leftElement && rightElement) {
  //       leftElement.style.top = `${positionTop}px`;
  //       rightElement.style.top = `${positionTop}px`;
  //     }
  //   }

  //   // Fixed footer
  //   const footerElement = document.querySelector('.app-footer');
  //   if (footerElement) {
  //     footerElement.classList.add('app-footer-fixed');
  //   }

  //   return () => {
  //     if (footerElement) {
  //       footerElement.classList.remove('app-footer-fixed');
  //     }
  //   };
  // }, [headerRef.current, isChangePosition]);

  return (
    <div
      // ref={headerRef}
      className={classNames('header-support-detail-page', state.isScroll && '--has-box-shadow')}
    >
      <DetailTitleComponent
        isLoadedData={isLoadedData}
        linkGoBack="/support"
        title={t`Support request`}
        titleBlockArray={titleBlockArray}
        rightComponent={generateRightButton()}
      />
    </div>
  );
};

export default HeaderPage;
